import {
  Button,
  IconButton,
  Input,
  InputAdornment,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField,
} from '@material-ui/core';
import clsx from 'clsx';
import {
  checkboxSelected,
  checkboxUnselected,
} from 'components/SelectIconComponent';
import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { CustomSelectOption, CustomSelectProps } from '../__types';
import { useCustomSelectStyle } from './style';
import { useRotateAnimation } from 'components/commons';
import CloseIcon from 'components/Icons/CloseIcon';
import RefreshIcon from 'components/Icons/RefreshIcon';
import { DownIcon } from 'components/Icons';

function CustomSelectEtapasProjetos({
  options,
  label,
  value,
  placeholder,
  multiple,
  error,
  helperText,
  onChange,
  onFocus,
  onBlur,
  disableFilter,
  loading,
  disabled,
}: CustomSelectProps) {
  const classes = useCustomSelectStyle();
  const rotate = useRotateAnimation();
  const [currentValue, setValue] = useState<any>(value);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [filter, setFilter] = useState<string>();
  const [labelColor, setLabelColor] = useState('initial');
  const textFieldRef = useRef<HTMLDivElement>(null);
  const [isMenuOpen, setMenuOpen] = useState(false);
  const [, setSelectedOptions] = useState<any[]>([]);

  const renderValue = useMemo(() => {
    if (multiple && currentValue && Array.isArray(currentValue)) {
      const itemCount = currentValue.length;
      const selectedLabel = itemCount > 1 ? 'selecionados' : 'selecionado';
      return itemCount !== 0 ? `${itemCount} ${selectedLabel}` : '';
    }
    if (currentValue !== undefined) {
      const current = options.find((option) => option.value === currentValue);
      return current?.selected || current?.label || '';
    }

    return '';
  }, [currentValue, multiple, options]);

  const handleSelectMultiple = useCallback(
    (option: CustomSelectOption, selected: boolean) => {
      const current =
        currentValue && Array.isArray(currentValue) ? [...currentValue] : [];
      if (selected) {
        const target = current.findIndex((item) => option.value === item);
        if (target > -1) {
          current.splice(target, 1);
          if (current.length === 0) {
            setValue(undefined);
            setSelectedOptions([]);
            onChange([]);
            return;
          }
        }
      } else {
        current.push(option.value);
      }
      setValue(current);
      setSelectedOptions(current);
      onChange(current);
    },
    [currentValue, onChange]
  );

  useEffect(() => {
    if (value && Array.isArray(value) && value.length === 0) {
      setValue(undefined);
    } else {
      setSelectedOptions(value || []);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (textFieldRef.current && !textFieldRef.current.contains(e.target as Node)) {
        setLabelColor(isMenuOpen ? '#47d6ff' : 'black');
        setMenuOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleBlur = () => {
    if (disabled) return;
    setLabelColor(isMenuOpen ? '#47d6ff' : 'black');
  };
  useEffect(() => {
    setValue(value);
  }, [value]);

  return (
    <>
      <TextField
        label={label}
        ref={textFieldRef}
        placeholder={placeholder}
        value={renderValue}
        onBlur={handleBlur}
        InputProps={{
          onClick: (e) => {
            if (disabled) return;
            setAnchor(e.currentTarget);
            onFocus && onFocus();
            setMenuOpen(true);
          },
          className: clsx(classes.input, {
            [classes.loading]: loading,
          }),
          endAdornment: (
            <InputAdornment position='end' className={classes.adornment}>
              {renderValue && !disabled && (
                <IconButton
                  type='button'
                  color='primary'
                  onClick={(e) => {
                    e.stopPropagation();
                    setValue([]);
                    onChange([]);
                    onBlur && onBlur();
                  }}
                  className={classes.adornmentButton}
                  disableRipple
                  disabled={disabled}
                  data-testid='CloseButton'
                >
                  <CloseIcon />
                </IconButton>
              )}
              <IconButton
                type='button'
                color='primary'
                className={classes.adornmentButton}
                disabled={disabled || loading}
                disableRipple
              >
                {loading ? (
                  <RefreshIcon className={rotate.complete} />
                ) : (
                  <DownIcon
                    className={clsx(
                      classes.adornmentIcon,
                      Boolean(anchor) && classes.adornmentIconRotate
                    )}
                  />
                )}
              </IconButton>
            </InputAdornment>
          ),
          readOnly: true,
        }}
        InputLabelProps={{
          shrink: Boolean(placeholder) || Boolean(currentValue),
          style: { color: labelColor },
        }}
        fullWidth
        error={error}
        helperText={helperText}
        disabled={disabled}
      />
      <Menu
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={() => {
          setAnchor(null);
          setFilter(undefined);
          onBlur && onBlur();
        }}
        PaperProps={{
          className: classes.menu,
          style: {
            width: anchor?.getBoundingClientRect().width,
            marginTop: (anchor?.getBoundingClientRect().height || 0) + 8,
          },
        }}
        MenuListProps={{
          style: {
            paddingTop: !disableFilter ? 0 : 8,
            paddingBottom: multiple ? 0 : 8,
          },
        }}
        variant='menu'
      >
        {!disableFilter && (
          <MenuItem
            className={classes.menuItemFilter}
            button={false}
            onKeyDown={(e) => e.stopPropagation()}
            onFocus={(e) => e.currentTarget.querySelector('input')?.focus()}
          >
            <Input
              placeholder='Pesquisar'
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              endAdornment={
                filter ? (
                  <InputAdornment position='end'>
                    <IconButton
                      color='primary'
                      style={{ fontSize: 20, padding: 2 }}
                      onClick={() => setFilter('')}
                      data-testid='closeFilter'
                    >
                      <CloseIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined
              }
              autoFocus
              fullWidth
            />
          </MenuItem>
        )}
        {options
          .filter((option) => {
            if (filter)
              return option.label.toLowerCase().includes(filter.toLowerCase());
            return true;
          })
          .map((option) => {
            const selected = Array.isArray(currentValue)
              ? currentValue.includes(option.value)
              : currentValue === option.value;
            return (
              <MenuItem
                key={option.value}
                className={classes.menuItem}
                onClick={() => {
                  if (multiple) {
                    handleSelectMultiple(option, selected);
                  } else {
                    setValue(option.value);
                    onChange(option.value);
                    setAnchor(null);
                    setFilter(undefined);
                    onBlur && onBlur();
                  }
                }}
                selected={selected}
              >
                {multiple && (
                  <ListItemIcon className={classes.menuItemCheckbox}>
                    {selected
                      ? checkboxSelected('small')
                      : checkboxUnselected('small')}
                  </ListItemIcon>
                )}
                {option.label}
              </MenuItem>
            );
          })}
        {multiple && (
          <MenuItem className={classes.menuItemActions} button={false}>
            <Button
              className={classes.btnTextSelectedAll}
              type='button'
              variant='text'
              color='primary'
              onClick={() => {
                const newValue = options.map((option) => option.value);
                setValue(newValue);
                onChange(newValue);
              }}
              disabled={currentValue && currentValue.length === options.length}
            >
              Selecionar tudo
            </Button>
            <Button
              className={classes.btnTextRemoveAll}
              type='button'
              variant='text'
              color='primary'
              onClick={() => {
                setValue([]);
                setSelectedOptions([]);
                onChange([]);
              }}
              disabled={!currentValue || !currentValue.length}
            >
              Limpar tudo
            </Button>
          </MenuItem>
        )}
      </Menu>
    </>
  );
}

export default CustomSelectEtapasProjetos;
